import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { orderAPI, authAPI } from "../../api";
import { Package, Search, Eye, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Check, ChevronDown } from "lucide-react";
import AccessModule from "../../components/AccessModule/AccessModule";
import ViewReturnOrder from "./ViewReturnOrder";
import "../Orders/Orders.css";
import { FaArrowRight } from "react-icons/fa";

const formatDateTime = (date: string) =>
  new Date(date)
    .toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(" at ", ", ")
    .toLowerCase();

export default function ReturnOrdersPage() {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(location.state?.status || "");
  const [isStatusOpen, setIsStatusOpen] = useState<boolean>(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>('_id');
  const [sortOrder, setSortOrder] = useState<'asc'|'desc'>('desc');
  const [viewProduct, setViewProduct] = useState<{ orderId: string; productId: string; _orderId: string } | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true); // Start loading

  const [accessChecked, setAccessChecked] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const userDetails = JSON.parse(
          localStorage.getItem("seller") || "{}"
        );

        const sellerRes = await authAPI.getSellerStatus(
          userDetails._id
        );

        setVerificationStatus(
          sellerRes.data.verification_status
        );

        setIsBlocked(
          sellerRes.data.isBlock
        );

        setAccessChecked(true);
      } catch (err) {
        console.error(err);
        setAccessChecked(true);
      }
    };

    checkAccess();
  }, []);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const res = await orderAPI.getReturnList({
        currentPage,
        itemsPerPage,
        search: searchQuery,
        status: statusFilter,
        sortBy,
        sortOrder
      });
      if (res.data.response === 'S') {
        // Flatten orders into individual returned products
        let flatProducts: any[] = [];
        res.data.data.item.forEach((order: any) => {
          (order.products || []).forEach((product: any) => {
            if (product.returnStatus) {
              const rs = (product.returnStatus || '').toLowerCase();
              let matches = true;
              if (statusFilter === 'requested') {
                matches = rs === 'requested';
              } else if (statusFilter === 'initiated') {
                matches = ['approved', 'initiated'].includes(rs);
              } else if (statusFilter === 'picked_up' || statusFilter === 'picked up') {
                matches = ['picked up', 'pickedup'].includes(rs);
              } else if (statusFilter === 'completed' || statusFilter === 'return_completed') {
                matches = ['received', 'refunded', 'completed'].includes(rs);
              }
              if (matches) {
                flatProducts.push({
                  ...product,
                  orderId: order.orderId,
                  _orderId: order._id,
                  customerName: order.customerName,
                  createdAt: order.createdAt,
                });
              }
            }
          });
        });
        setOrders(flatProducts);
        setTotalCount(res.data.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Failed to fetch return orders", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (!accessChecked) return;

      if (
        isBlocked ||
        verificationStatus === "P" ||
        verificationStatus === "R"
      ) {
        return;
      }
      fetchReturns();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, sortBy, sortOrder, accessChecked, isBlocked, verificationStatus]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getReturnStatusLabel = (status: string) => {
    const s = (status || '').toLowerCase().trim();
    if (s === 'requested') return 'Requested';
    if (s === 'approved' || s === 'initiated') return 'Initiated';
    if (s === 'picked up' || s === 'pickedup') return 'Picked Up';
    if (s === 'received' || s === 'refunded' || s === 'completed') return 'Return Completed';
    return status || 'Requested';
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase().trim();
    if (s === 'requested') return 'ord-badge ord-badge-processing';
    if (s === 'approved' || s === 'initiated') return 'ord-badge ord-badge-return-initiated';
    if (s === 'picked up' || s === 'pickedup') return 'ord-badge ord-badge-picked-up';
    if (s === 'received' || s === 'refunded' || s === 'completed') return 'ord-badge ord-badge-return-completed';
    return 'ord-badge ord-badge-default';
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

  const getPaginationGroup = () => {
    const pages: (number | string)[] = [];
    const maxPages = Number(totalPages) || 1;
    const currPage = Number(currentPage) || 1;
    if (maxPages <= 8) {
      for (let i = 1; i <= maxPages; i++) pages.push(i);
    } else {
      const lb = 2, rb = 2;
      let sp = Math.max(lb + 1, currPage - 1);
      let ep = Math.min(maxPages - rb, currPage + 1);
      if (currPage <= lb + 2) ep = lb + 3;
      if (currPage >= maxPages - rb - 1) sp = maxPages - rb - 2;
      for (let i = 1; i <= lb; i++) pages.push(i);
      if (sp > lb + 1) pages.push("...");
      for (let i = sp; i <= ep; i++) pages.push(i);
      if (ep < maxPages - rb) pages.push("...");
      for (let i = maxPages - rb + 1; i <= maxPages; i++) pages.push(i);
    }
    return pages;
  };


  if (
    isBlocked ||
    verificationStatus === "P" ||
    verificationStatus === "R"
  ) {
    return (
      <AccessModule
        isBlock={isBlocked}
        verification_status={verificationStatus}
      />
    );
  }

  if (viewProduct) {
    return (
      <ViewReturnOrder
        orderId={viewProduct._orderId}
        productId={viewProduct.productId}
        onBack={() => { setViewProduct(null); fetchReturns(); }}
      />
    );
  }

  return (
    <div className="ord-page fade-in">
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 className="page-title">Return Products</h1>
          <p className="page-subtitle">Manage customer returns and refunds</p>
        </div>
      </div>

      <div className="ord-card" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 210px)', minHeight: '500px' }}>
        {/* ── Filter Bar ── */}
        <div className="ord-filter-bar" style={{ flexShrink: 0 }}>
          <div className="ord-filter-left" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            {/* Search */}
            <div className="ord-search-wrap">
              <Search className="ord-search-icon" size={16} />
              <input
                type="text"
                placeholder="Search Order ID or Product…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value.trimStart());
                  setCurrentPage(1);
                }}
                className="ord-search-input"
              />
            </div>

            {/* Status Filter Tabs */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {/* MOBILE DROPDOWN */}
              <div className="hide-on-desktop" style={{ position: 'relative', width: 'auto', minWidth: '160px' }}>
                <button 
                  onClick={() => setIsStatusOpen(!isStatusOpen)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '11px 16px', background: 'white', border: '1px solid #e2e8f0',
                    borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s',
                    color: '#334155', fontWeight: '600', gap: '8px', height: '100%', width: '100%'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      width: '8px', height: '8px', borderRadius: '50%', 
                      background: statusFilter === '' ? '#94a3b8' : statusFilter === 'requested' ? '#f59e0b' : statusFilter === 'initiated' ? '#ea580c' : statusFilter === 'picked_up' ? '#8b5cf6' : '#10b981' 
                    }} />
                    {statusFilter === '' ? 'All Returns' : statusFilter === 'requested' ? 'Requested' : statusFilter === 'initiated' ? 'Initiated' : statusFilter === 'picked_up' ? 'Picked Up' : 'Return Completed'}
                  </div>
                  <ChevronDown size={16} strokeWidth={2.5} style={{ 
                    transform: isStatusOpen ? 'rotate(180deg)' : 'rotate(0deg)', 
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                    color: '#94a3b8' 
                  }} />
                </button>
                
                {isStatusOpen && (
                  <>
                    <div onClick={() => setIsStatusOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                    <div className="fade-in" style={{
                      position: 'absolute', top: 'calc(100% + 8px)', right: 0, left: 0,
                      background: 'white', borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)',
                      border: '1px solid #f1f5f9', padding: '6px', zIndex: 50,
                    }}>
                      {[
                        { value: '', label: 'All Returns' },
                        { value: 'requested', label: 'Requested' },
                        { value: 'initiated', label: 'Initiated' },
                        { value: 'picked_up', label: 'Picked Up' },
                        { value: 'completed', label: 'Return Completed' }
                      ].map(s => (
                        <button
                          key={s.value}
                          onClick={() => { setStatusFilter(s.value); setIsStatusOpen(false); setCurrentPage(1); }}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 12px', borderRadius: '8px', border: 'none', background: 'transparent',
                            cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                            color: statusFilter === s.value ? '#14b8a6' : '#475569',
                            fontWeight: statusFilter === s.value ? '700' : '500',
                            fontSize: '14px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ 
                              width: '8px', height: '8px', borderRadius: '50%',
                              background: s.value === '' ? '#94a3b8' : s.value === 'requested' ? '#f59e0b' : s.value === 'initiated' ? '#ea580c' : s.value === 'picked_up' ? '#8b5cf6' : '#10b981' 
                            }} />
                            {s.label}
                          </div>
                          {statusFilter === s.value && <Check size={14} strokeWidth={3} />}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* DESKTOP TABS */}
              <div className="ord-tabs-container hide-on-mobile" style={{ display: 'flex', gap: '8px' }}>
                {[
                  { value: '', label: 'All Returns' },
                  { value: 'requested', label: 'Requested' },
                  { value: 'initiated', label: 'Initiated' },
                  { value: 'picked_up', label: 'Picked Up' },
                  { value: 'completed', label: 'Return Completed' }
                ].map(s => (
                  <button
                    key={s.value}
                    onClick={() => {
                      setStatusFilter(s.value);
                      setCurrentPage(1);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', borderRadius: '20px', whiteSpace: 'nowrap',
                      border: statusFilter === s.value ? 'none' : '1px solid #e2e8f0',
                      background: statusFilter === s.value 
                        ? (s.value === '' ? '#334155' : s.value === 'requested' ? '#f59e0b' : s.value === 'initiated' ? '#ea580c' : s.value === 'picked_up' ? '#8b5cf6' : '#10b981')
                        : 'white',
                      color: statusFilter === s.value ? 'white' : '#64748b',
                      fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: statusFilter === s.value ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    {statusFilter === s.value && <Check size={14} strokeWidth={3} />}
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {(searchQuery || statusFilter) && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("");
                  setCurrentPage(1);
                }}
                className="ord-btn-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div className="ord-table-wrap products-table-container" style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', display: 'block' }}>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' }}>
                <div style={{ width: '36px', height: '36px', border: '3px solid #f1f5f9', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: '15px', fontWeight: '500', color: '#475569' }}>Fetching return orders...</span>
              </div>
            ) : orders.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', width: '100%', background: '#fff', borderRadius: '12px' }}>
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px' }}>
                  <path d="M53.3333 18.6667H29.3333L24 13.3333H10.6667C7.72 13.3333 5.33333 15.72 5.33333 18.6667V45.3333C5.33333 48.28 7.72 50.6667 10.6667 50.6667H53.3333C56.28 50.6667 58.6667 48.28 58.6667 45.3333V24C58.6667 21.0533 56.28 18.6667 53.3333 18.6667Z" fill="url(#return-folder-grad)"/>
                  <defs>
                    <linearGradient id="return-folder-grad" x1="5.33333" y1="13.3333" x2="58.6667" y2="50.6667" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FCD34D"/>
                      <stop offset="1" stopColor="#F59E0B"/>
                    </linearGradient>
                  </defs>
                </svg>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', fontFamily: 'inherit' }}>
                  No returned products found
                </h3>
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', maxWidth: '400px', textAlign: 'center' }}>
                  No records match the selected category, dates, or search term.
                </p>
                {(searchQuery || statusFilter) && (
                  <button
                    onClick={() => { setSearchQuery(''); setStatusFilter(''); setCurrentPage(1); }}
                    style={{ background: '#2563eb', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#1d4ed8'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#2563eb'}
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <table className="ord-table" style={{ margin: 0, width: '100%', minWidth: '800px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th className="ord-col-id" onClick={() => handleSort('orderId')} style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', width: '15%', cursor: 'pointer' }}>
                      Order ID {sortBy === 'orderId' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : ''}
                    </th>
                    <th className="ord-col-cust" style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', width: '30%' }}>Product</th>
                    <th className="ord-col-amt" onClick={() => handleSort('customerName')} style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', width: '15%', cursor: 'pointer' }}>
                      Customer {sortBy === 'customerName' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : ''}
                    </th>
                    <th className="ord-col-date" style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', width: '15%' }}>Reason</th>
                    <th className="ord-col-status" style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', width: '15%' }}>Status</th>
                    <th className="ord-col-action" style={{ padding: '16px 24px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', width: '10%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td className="ord-col-id" style={{ padding: '16px 24px', width: '15%' }}>
                        <span className="ord-id" style={{ fontWeight: '600', color: '#475569' }}>{item.orderId}</span>
                      </td>

                      <td className="ord-col-cust" style={{ padding: '16px 24px', width: '30%' }}>
                        <div className="ord-customer" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {item.image && typeof item.image === 'string' && item.image !== '[object Object]' ? (
                            <>
                              <img 
                                src={item.image} 
                                alt="" 
                                style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} 
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                              <div style={{ display: 'none', width: '40px', height: '40px', borderRadius: '8px', background: '#f1f5f9', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                                <Package size={20} color="#94a3b8" />
                              </div>
                            </>
                          ) : (
                            <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                              <Package size={20} color="#94a3b8" />
                            </div>
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span
                              className="ord-cust-name"
                              style={{ fontWeight: '500', color: '#1e293b' }}
                              title={item.productName}
                            >
                              {item.productName
                                ? item.productName.length > 30
                                  ? `${item.productName.substring(0, 30)}...`
                                  : item.productName
                                : ''}
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>Qty: {item.quantity}</span>
                          </div>
                        </div>
                      </td>

                      <td className="ord-col-amt" style={{ padding: '16px 24px', width: '15%' }}>
                        <span className="ord-cust-name" style={{ fontWeight: '500', color: '#1e293b' }}>{item.customerName}</span>
                      </td>

                      {/* <td className="ord-col-date" style={{ padding: '16px 24px', width: '15%' }}>
                        <span className="ord-date" style={{ color: '#ef4444', fontSize: '14px' }}>{item.returnReason || 'N/A'}</span>
                      </td> */}
 
                         <td className="ord-col-date" style={{ padding: '16px 24px', width: '15%' }}>
                              <span
                                className="ord-date"
                                style={{ color: '#ef4444', fontSize: '14px' }}
                                title={item.returnReason}
                              >
                                {item.returnReason
                                  ? item.returnReason.length > 20
                                    ? `${item.returnReason.substring(0, 20)}...`
                                    : item.returnReason
                                  : 'N/A'}
                              </span>
                            </td>

                      <td className="ord-col-status" style={{ padding: '16px 24px', width: '15%' }}>
                        <span className={getStatusColor(item.returnStatus)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title={item.returnStatus}
                        >
                          {getReturnStatusLabel(item.returnStatus)}
                        </span>
                      </td>

                      <td className="ord-col-action" style={{ padding: '16px 24px', width: '10%' }}>
                        <div className="ord-action-group">
                          <button
                            className="ord-view-btn"
                            title="View Return Details"
                            onClick={() => setViewProduct({ orderId: item.orderId, productId: item.productId, _orderId: item._orderId })}
                            style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#2563eb' }}
                          >
                            <FaArrowRight size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>

        {/* ── Footer / Pagination ── */}
        <div className="ord-footer" style={{ flexShrink: 0 }}>
          <div className="ord-footer-left">
            <span className="ord-page-info">
              Showing{" "}
              {totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
              {" "}–{" "}
              {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
            </span>
            <div className="ord-per-page-wrap">
              <span>Per page:</span>
              <select
                className="ord-per-page-select"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <div className="ord-pagination">
            <button
              className="ord-pg-btn"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={Number(currentPage) <= 1}
              title="Previous"
            >
              <ChevronLeft size={16} />
            </button>

            {getPaginationGroup().map((pg, i) =>
              pg === "..." ? (
                <span key={`ellipsis-${i}`} className="ord-pg-ellipsis">
                  …
                </span>
              ) : (
                <button
                  key={pg}
                  className={`ord-pg-btn${Number(currentPage) === Number(pg) ? " active" : ""}`}
                  onClick={() => typeof pg === "number" && setCurrentPage(pg)}
                >
                  {pg}
                </button>
              )
            )}

            <button
              className="ord-pg-btn"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages || 1))}
              disabled={Number(currentPage) >= (Number(totalPages) || 1)}
              title="Next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
